/** Tutorial Flow 3 pipeline steps — concept tree through curriculum optimization. */

export const FLOW3_PIPELINE_STEPS = [
  {
    id: 'concept_tree_v3',
    shortLabel: 'Concept tree',
    label: 'Step 0 — Universal concept tree',
    description: 'Hierarchical concept tree with learning path, dependencies, and Feynman explanation.',
    promptParts: ['ROOT', 'Level 1–3 branches', 'A–J sections', 'JSON concept_tree'],
  },
  {
    id: 'knowledge_analysis_v3',
    shortLabel: 'Knowledge analysis',
    label: 'Step 1 — Knowledge Analysis Engine',
    description: 'Concept Intelligence Report: dependencies, cognitive load, artifacts, slides — metadata only.',
    promptParts: [
      'Part 1 — Identification',
      'Part 2 — Explanation',
      'Part 3 — Dependencies',
      'Part 4 — Dependency graph',
      'Part 5 — Cognitive complexity',
      'Part 6 — Learning stage',
      'Part 7 — Teaching artifacts',
      'Part 8 — Visualization',
      'Part 9 — Examples',
      'Part 10 — Misconceptions',
      'Part 11 — Practicality',
      'Part 12 — Slide estimate',
      'Part 13 — Granularity',
      'Part 14 — Learning path metadata',
      'Part 15 — Per-concept JSON',
      'Final report',
    ],
  },
  {
    id: 'course_structure_v3',
    shortLabel: 'Course structure',
    label: 'Step 2 — Course Structure',
    description: 'Organize concepts into modules and learning sequence — no slide content.',
    promptParts: ['Module grouping', 'Learning sequence', 'Concept mapping', 'Structure summary'],
  },
  {
    id: 'slide_architecture_v3',
    shortLabel: 'Slide architecture',
    label: 'Step 3 — Slide Architecture Plan',
    description: 'Per-slide objectives, artifacts, and explanation layers — metadata only.',
    promptParts: ['Slide list', 'Per-concept grouping', 'Artifact assignment', 'Slide statistics'],
  },
  {
    id: 'curriculum_optimization_v3',
    shortLabel: 'Optimize (3.5)',
    label: 'Step 3.5 — Course Structure Optimization',
    description: 'Validate coverage, repair gaps, remove redundancy, optimize artifacts and slide distribution.',
    promptParts: [
      'Phase 1 — Coverage audit',
      'Phase 2 — Decomposition',
      'Phase 3 — Hidden knowledge',
      'Phase 4 — Redundancy',
      'Phase 5 — Overlap',
      'Phase 6 — Learning flow',
      'Phase 7 — Artifacts',
      'Phase 8 — Depth',
      'Phase 9 — Distribution',
      'Phase 10 — Cognitive load',
      'Phase 11 — Completeness',
      'Phase 12 — Optimized outputs',
    ],
  },
  {
    id: 'artifact_specification_v3',
    shortLabel: 'Artifact specs',
    label: 'Step 4 — Artifact Production Specification',
    description: 'Per-slide, per-artifact production specs: purpose, complexity, visual elements, teaching value.',
    promptParts: [
      'Required artifacts',
      'Artifact purpose',
      'Artifact complexity',
      'Generation instructions',
      'Expected visual elements',
      'Expected teaching value',
      'Analogy (when applicable)',
    ],
  },
  {
    id: 'artifact_generation_v3',
    shortLabel: 'AKOs (5A)',
    label: 'Step 5A — Artifact Knowledge Object (AKO) Generation Engine',
    description: 'Semantic AKOs per artifact — educational, visual, interaction, and narration models (no images/SVG/PNG).',
    promptParts: [
      'AKO envelope',
      'Educational metadata',
      'Semantic model',
      'Visual model',
      'Interaction model',
      'Narration model',
      'Explanation · Diagram · Flowchart',
      'Table · Analogy · Example · Code · Algorithm',
      'Assessment & rendering',
      'Quality check',
    ],
  },
  {
    id: 'artifact_rendering_v3',
    shortLabel: 'ARP (5B)',
    label: 'Step 5B — Artifact Rendering Plan (ARP)',
    description: 'Rendering plans from AKOs — SVG specs, layers, coordinates, animation and accessibility metadata.',
    promptParts: [
      'ARP envelope',
      'Spatial layout',
      'Diagram ARP',
      'Flowchart · Table · Graph · Code',
      'Analogy · Example · Visual demo',
      'Animation metadata',
      'Accessibility',
      'Quality check',
    ],
  },
  {
    id: 'artifact_edm_v3',
    shortLabel: 'EDM (5C.1)',
    label: 'Step 5C.1 — Editable Diagram Model (EDM)',
    description: 'Canonical editable graph — nodes, edges, layout config, React Flow export. No SVG; source of truth.',
    promptParts: [
      'EDM root artifact',
      'Node model',
      'Edge model',
      'Layout config',
      'Graph validation',
      'React Flow export',
      'Version snapshot',
    ],
  },
  {
    id: 'artifact_authoring_v3',
    shortLabel: 'Authoring (5C.2)',
    label: 'Step 5C.2 — Artifact Authoring API Layer',
    description: 'Publish EDMs to versioned authoring store — CRUD, optimistic locking, full snapshot versioning.',
    promptParts: [
      'Publish EDMs',
      'Artifact CRUD API',
      'Draft session + published versions',
      'Draft Session API',
      'Batch editing API',
      'Layout preview / apply',
      'Artifact edit lock',
      'Change log',
      'Node/Edge editing',
      'Version snapshots',
      'Optimistic locking',
      'Graph validation',
      'Restore versions',
    ],
  },
  {
    id: 'artifact_studio_v3',
    shortLabel: 'Studio (5C.3)',
    label: 'Step 5C.3 — Artifact Authoring Studio',
    description: 'React Flow visual editor — create, edit, version, and manage EDM artifacts.',
    promptParts: [
      'Artifact Editor Page',
      'EDM ↔ React Flow',
      'Node palette',
      'Undo / Redo',
      'Version sidebar',
      'Auto layout',
      'Save workflow',
      'Keyboard shortcuts',
    ],
  },
  {
    id: 'artifact_layout_engine_v3',
    shortLabel: 'Layout (5C.4)',
    label: 'Step 5C.4 — Layout Engine Service',
    description: 'Automatic layout computation — ELK, hierarchical, tree, radial, force. Preview and apply coordinates.',
    promptParts: [
      'Layout Engine Interface',
      'ELK Adapter',
      'Preview API',
      'Apply API',
      'Quality metrics',
      'React Flow output',
      'Layout cache',
    ],
  },
  {
    id: 'artifact_svg_renderer_v3',
    shortLabel: 'SVG (5C.5)',
    label: 'Step 5C.5 — SVG Rendering Engine',
    description: 'Convert positioned EDM into professional SVG — nodes, edges, themes, viewport, interactivity metadata.',
    promptParts: [
      'SVG Renderer',
      'Node shapes',
      'Edge routing',
      'Themes',
      'Viewport',
      'Interactivity metadata',
      'Render API',
    ],
  },
  {
    id: 'artifact_rrao_v3',
    shortLabel: 'RRAO (5C.6)',
    label: 'Step 5C.6 — Runtime Rendered Artifact Object (RRAO)',
    description: 'Package SVG, regions, anchors, camera/animation targets, and semantic metadata for runtime engines.',
    promptParts: [
      'RRAO Builder',
      'Node regions',
      'Edge regions',
      'Interaction zones',
      'Narration anchors',
      'Camera targets',
      'Animation targets',
      'Semantic analysis',
      'Serialization',
      'RRAO API',
    ],
  },
];

/** Backend route suffix per step (POST). */
const FLOW3_API_SUFFIX = {
  concept_tree_v3: 'concept-tree',
  knowledge_analysis_v3: 'knowledge-analysis',
  course_structure_v3: 'course-structure',
  slide_architecture_v3: 'slide-architecture',
  curriculum_optimization_v3: 'curriculum-optimization',
  artifact_specification_v3: 'artifact-specification',
  artifact_generation_v3: 'artifact-generation',
  artifact_rendering_v3: 'artifact-rendering',
  artifact_edm_v3: 'artifact-edm',
  artifact_authoring_v3: 'artifact-authoring',
  artifact_studio_v3: 'artifact-studio',
  artifact_layout_engine_v3: 'artifact-layout-engine',
  artifact_svg_renderer_v3: 'artifact-svg-renderer',
  artifact_rrao_v3: 'artifact-rrao',
};

/** API path shown in pipeline outline JSON panel. */
export function flow3StepApiEndpoint(stepId, projectId = null) {
  const suffix = FLOW3_API_SUFFIX[stepId];
  if (!suffix) return null;
  const base = '/learning-tutorials/flows/v3';
  if (projectId) {
    return {
      method: 'POST',
      path: `${base}/projects/${projectId}/${suffix}`,
      pathTemplate: `${base}/projects/{projectId}/${suffix}`,
    };
  }
  return {
    method: 'POST',
    path: `${base}/${suffix}`,
    pathTemplate: `${base}/${suffix}`,
  };
}

export function flow3StepStatus(
  stepId,
  {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
    pipelineStatus,
    currentStepId,
  },
) {
  const working = pipelineStatus === 'working';
  const check = (id, done) => {
    if (working && currentStepId === id) return 'active';
    if (done) return 'done';
    return 'pending';
  };

  if (stepId === 'concept_tree_v3') {
    return check(stepId, Boolean(conceptTree?.concept_tree));
  }
  if (stepId === 'knowledge_analysis_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (knowledgeAnalysis?.concepts?.length) return 'done';
    if (!conceptTree?.concept_tree) return 'pending';
    return 'ready';
  }
  if (stepId === 'course_structure_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (courseStructure?.modules?.length) return 'done';
    if (!knowledgeAnalysis?.concepts?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'slide_architecture_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (slideArchitecture?.slides?.length) return 'done';
    if (!courseStructure?.modules?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'curriculum_optimization_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (curriculumOptimization?.updated_slide_architecture) return 'done';
    if (!slideArchitecture?.slides?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_specification_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactSpecification?.slide_specifications?.length) return 'done';
    if (!curriculumOptimization?.updated_slide_architecture?.slides?.length
      && !curriculumOptimization?.final_curriculum_readiness_score) return 'pending';
    if (!curriculumOptimization) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_generation_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactGeneration?.artifact_knowledge_objects?.length) return 'done';
    if (!artifactSpecification?.slide_specifications?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_rendering_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactRendering?.rendered_artifact_objects?.length) return 'done';
    if (!artifactGeneration?.artifact_knowledge_objects?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_edm_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactEdm?.editable_diagram_models?.length) return 'done';
    if (!artifactRendering?.rendered_artifact_objects?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_authoring_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactAuthoring?.published_artifacts?.length) return 'done';
    if (!artifactEdm?.editable_diagram_models?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_studio_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactStudio?.studio_entries?.length) return 'done';
    if (!artifactAuthoring?.published_artifacts?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_layout_engine_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactLayoutEngine?.layout_entries?.length) return 'done';
    if (!artifactStudio?.studio_entries?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_svg_renderer_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactSvgRenderer?.render_entries?.length) return 'done';
    if (!artifactLayoutEngine?.layout_entries?.length) return 'pending';
    return 'ready';
  }
  if (stepId === 'artifact_rrao_v3') {
    if (working && currentStepId === stepId) return 'active';
    if (artifactRrao?.rrao_entries?.length) return 'done';
    if (!artifactSvgRenderer?.render_entries?.length) return 'pending';
    return 'ready';
  }
  return 'pending';
}

export function flow3StepData(
  stepId,
  {
    conceptTree,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    artifactLayoutEngine,
    artifactSvgRenderer,
    artifactRrao,
  },
) {
  if (stepId === 'concept_tree_v3') return conceptTree;
  if (stepId === 'knowledge_analysis_v3') return knowledgeAnalysis;
  if (stepId === 'course_structure_v3') return courseStructure;
  if (stepId === 'slide_architecture_v3') return slideArchitecture;
  if (stepId === 'curriculum_optimization_v3') return curriculumOptimization;
  if (stepId === 'artifact_specification_v3') return artifactSpecification;
  if (stepId === 'artifact_generation_v3') return artifactGeneration;
  if (stepId === 'artifact_rendering_v3') return artifactRendering;
  if (stepId === 'artifact_edm_v3') return artifactEdm;
  if (stepId === 'artifact_authoring_v3') return artifactAuthoring;
  if (stepId === 'artifact_studio_v3') return artifactStudio;
  if (stepId === 'artifact_layout_engine_v3') return artifactLayoutEngine;
  if (stepId === 'artifact_svg_renderer_v3') return artifactSvgRenderer;
  if (stepId === 'artifact_rrao_v3') return artifactRrao;
  return null;
}
